import { useEffect, useState } from 'react';
import { callDeepLink } from '@croquiscom/web2app';
import { useBottomSheet } from '@croquiscom-pvt/zds';

import { gotoLogin } from '@common/utils';
import {
  PopupViewType,
  useCatalogProductIdParams,
  useIsAuthenticated,
  usePdpBaseInfo,
  usePdpConfig,
  usePopupView,
} from '@domains/pdp-common';
import { useMaximumBenefit } from '@domains/pdp-common';

import { BottomSheetMileageGradeContent } from '../components/BottomSheetMileageGradeContent';
import { useUserBenefitInfoQuery } from '../query/useUserBenefitInfoQuery';
import { useUserMembershipQuery } from '../query/useUserMembershipQuery';
import { CreditRewards, Message } from '../types';
import { getEasyPaymentRewardMessage } from '../utils/getEasyPaymentRewardMessage';
import { getMileageRewardMessage } from '../utils/getMileageRewardMessage';
import { getPointRewardMessage } from '../utils/getPointRewardMessage';
import { makeFinalPrice } from '../utils/makeFinalPrice';

interface FinalPriceParam {
  benefitFinalPrice: number | null;
  membershipFinalPrice: number | null;
}

export const useCreditRewardsServices = () => {
  const { isLoggedIn } = useIsAuthenticated();
  const { isZigzagApp } = usePdpConfig();
  const { showPopup } = usePopupView();
  const catalogProductIdParams = useCatalogProductIdParams();
  const mileageRewardMessage = getMileageRewardMessage();
  const pointRewardMessage = getPointRewardMessage();
  const easyPaymentRewardMessage = getEasyPaymentRewardMessage();
  const { open: openBottomSheet } = useBottomSheet('custom');

  const {
    product: {
      product_price: { first_order_discount },
    },
    product,
    shop,
    brand_home,
    flags: { is_selected_shop },
  } = usePdpBaseInfo(catalogProductIdParams);
  const { product_option_list, isFetched } = useMaximumBenefit();
  const isMultipleOption = product_option_list.some(({ value_list }) => value_list.length > 1);

  const [bottomSheetOpen, setBottomSheetOpen] = useState<boolean>(false);
  const [finalPrice, setFinalPrice] = useState<FinalPriceParam>(
    makeFinalPrice({
      isMultipleOption,
      first_order_discount: first_order_discount?.price,
      final_price: product.product_price.final_discount_info.discount_price,
      promotion_quantity: product.promotion_info?.bogo_required_quantity,
    }),
  );

  const benefitParam = {
    catalog_product_id: product.id,
    final_price: finalPrice.benefitFinalPrice,
    withMembership: shop.is_mileage_shop,
  };

  const membershipParam = {
    catalog_product_id: product.id,
    final_price: finalPrice.membershipFinalPrice,
  };

  const {
    pdp_user_base_info: {
      pdp_user_membership: IntegratedQueryMembership,
      pdp_max_user_benefit,
      pdp_user_additional_mileage: pdpUserAdditionalMileage,
      pdp_user_nonmember_membership: pdpUserNonmemberMembership,
    },
  } = useUserBenefitInfoQuery(benefitParam);

  const {
    pdp_user_base_info: { pdp_user_membership: singleQueryMembership },
  } = useUserMembershipQuery(membershipParam);

  const pdpUserMembership = singleQueryMembership ?? IntegratedQueryMembership;
  const maximumReviewBonus = pdp_max_user_benefit?.issuable_mileage?.review?.maximum_review_bonus ?? 0;
  const mileagePromotionAmount = pdp_max_user_benefit?.issuable_mileage?.promotion?.amount ?? 0;
  const mileageRewardAmount = pdpUserMembership?.mileage_reward?.amount ?? 0;
  const reviewWrite = Math.max(
    pdp_max_user_benefit?.issuable_point?.review?.text ?? 0,
    pdp_max_user_benefit?.issuable_point?.review?.photo ?? 0,
  );

  const totalRewardPoint =
    (pdp_max_user_benefit?.issuable_point?.review?.best ?? 0) +
    (pdp_max_user_benefit?.issuable_point?.review?.first ?? 0) +
    (pdp_max_user_benefit?.issuable_point?.purchase?.first_order ?? 0) +
    (pdp_max_user_benefit?.issuable_point?.purchase?.decision ?? 0) +
    reviewWrite;

  const totalRewardMileage =
    mileageRewardAmount +
    maximumReviewBonus +
    mileagePromotionAmount +
    (isLoggedIn ? 0 : (pdpUserNonmemberMembership?.amount ?? 0));

  const getMileageContentList = (): CreditRewards[] => {
    if (!totalRewardMileage) {
      return [];
    }

    const contents: Message[] = [];

    const issuableMileage = pdp_max_user_benefit?.issuable_mileage;

    if (issuableMileage?.promotion) {
      contents.push(mileageRewardMessage.getPromotionMessage(issuableMileage.promotion));
    }

    if (shop.is_mileage_shop && pdpUserMembership && Boolean(mileageRewardAmount)) {
      contents.push({
        ...mileageRewardMessage.getMembershipMessage(
          pdpUserAdditionalMileage?.additional_rate ?? 0,
          pdpUserMembership!,
        ),
        onClickInfo: handleClickMileageInfo,
      });
    }

    // 미로그인 노출
    if (shop.is_mileage_shop && !isLoggedIn && Boolean(pdpUserNonmemberMembership?.amount)) {
      contents.push({
        ...mileageRewardMessage.getNonmemberMembershipMessage(
          pdpUserNonmemberMembership?.rate ?? 0,
          pdpUserNonmemberMembership?.amount ?? 0,
          handleClickRedirectLogin,
        ),
        onClickInfo: handleClickMileageInfo,
      });
    }

    if (maximumReviewBonus) {
      contents.push(mileageRewardMessage.getReviewBonusMessage(maximumReviewBonus));
    }

    return [
      {
        title: {
          ...mileageRewardMessage.getMileageTitle(totalRewardMileage),
          badge: pdpUserAdditionalMileage?.badge_text.text ?? '',
        },
        contents,
      },
    ];
  };

  const getPointContentList = (): CreditRewards[] => {
    if (!totalRewardPoint) {
      return [];
    }

    const contents: Message[] = [];

    if (pdp_max_user_benefit?.issuable_point?.purchase?.decision) {
      contents.push(
        pointRewardMessage.getDecisionMessage(
          brand_home?.name ?? shop.name,
          pdp_max_user_benefit.issuable_point.purchase.decision,
        ),
      );
    }

    if (pdp_max_user_benefit?.issuable_point?.purchase?.first_order) {
      contents.push(pointRewardMessage.getFirstOrderMessage(pdp_max_user_benefit.issuable_point.purchase.first_order));
    }

    if (reviewWrite) {
      contents.push(pointRewardMessage.getFirstOrderMessage(reviewWrite));
    }

    if (pdp_max_user_benefit?.issuable_point?.review?.first) {
      contents.push(pointRewardMessage.getFirstReviewMessage(pdp_max_user_benefit.issuable_point.review.first));
    }

    if (pdp_max_user_benefit?.issuable_point?.review?.best) {
      contents.push(pointRewardMessage.getBestReviewMessage(pdp_max_user_benefit?.issuable_point?.review?.best));
    }

    return [{ title: pointRewardMessage.getPointTitle(totalRewardPoint), contents }];
  };

  const getEasyPaymentContentList = (): CreditRewards[] => {
    if (!pdp_max_user_benefit?.issuable_point?.easy_payment?.point) {
      return [];
    }

    const contents: Message[] = [];
    const reward = Number(pdp_max_user_benefit.issuable_point.easy_payment.point);
    const icon_url = pdp_max_user_benefit.issuable_point.easy_payment.prefix_icon_url ?? '';
    const color = is_selected_shop ? '#FF4C34' : '';

    return [
      {
        title: easyPaymentRewardMessage.getEasyPaymentTitle(reward, icon_url, color),
        contents,
      },
    ];
  };

  const handleClickRedirectLogin = () => {
    if (isZigzagApp) {
      callDeepLink('/login')();
      return false;
    }

    gotoLogin({ redirect_url: window.location.href });
  };

  const handleClickCloseBottomSheet = () => {
    setBottomSheetOpen(false);
  };

  const handleClickMileageInfo = () => {
    showPopup(PopupViewType.MILEAGE, () => {
      setBottomSheetOpen(true);
      openBottomSheet({
        maxHeight: '80vh',
        children: (
          <div style={{ padding: '0 16px 16px 16px' }}>
            <BottomSheetMileageGradeContent />
          </div>
        ),
      });
    });
  };

  useEffect(() => {
    if (!isFetched) {
      return;
    }

    setFinalPrice(
      makeFinalPrice({
        isMultipleOption,
        first_order_discount: first_order_discount?.price,
        final_price: product.product_price.final_discount_info.discount_price,
        promotion_quantity: product.promotion_info?.bogo_required_quantity,
      }),
    );
  }, [
    product.product_price.final_discount_info.discount_price,
    product.promotion_info?.bogo_required_quantity,
    first_order_discount?.price,
    isFetched,
    isMultipleOption,
  ]);

  return {
    reward: [...getMileageContentList(), ...getPointContentList(), ...getEasyPaymentContentList()],
    totalRewardPoint,
    isBottomSheetOpen: bottomSheetOpen,
    handleClickRedirectLogin,
    handleClickCloseBottomSheet,
    handleClickMileageInfo,
  };
};
